import React, { useEffect, useState } from "react";
import { api } from "../../api";
import { DocumentVisibilityModalProps } from "../../types/Modal";
import { ResourceRole } from "../../types/ResourceRole";
import { capitalize } from "../../utils/helpers";
import RedButton from "../Buttons/RedButton";
import Loader from "../Loader";

const DocumentVisibilityModal: React.FC<DocumentVisibilityModalProps> = ({
  transactionId,
  selectedDoc,
  handleSubmit,
}) => {
  const [roles, setRoles] = useState<ResourceRole[]>([]);
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    (async () => {
      if (!selectedDoc) return;

      setIsLoading(true);
      try {
        const [transactionRoles, visibleRoleIds] = await Promise.all([
          api.transactionRoles.getTransactionRoles(),
          api.transactions.getTransactionDocumentVisibility(
            transactionId,
            selectedDoc.id
          ),
        ]);

        setRoles(
          transactionRoles.sort((a, b) => a.name.localeCompare(b.name))
        );
        setSelectedRoleIds(visibleRoleIds);
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [selectedDoc, transactionId]);

  const toggleRole = (roleId: string) => {
    if (selectedRoleIds.includes(roleId)) {
      setSelectedRoleIds(selectedRoleIds.filter((id) => id !== roleId));
    } else {
      setSelectedRoleIds([...selectedRoleIds, roleId]);
    }
  };

  const saveVisibility = async () => {
    setIsSaving(true);
    try {
      await handleSubmit(selectedRoleIds);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex flex-col text-center items-center bg-white w-full min-h-288 h-auto py-6 px-8 md:w-423">
      {isLoading && <Loader />}
      <h1 className="text-lg md:text-xxl text-xxx-gray mt-2 mb-6">
        Document Visibility
      </h1>

      <div className="w-full flex flex-col items-start">
        <p className="text-xs font-semibold tracking-extra text-welcome-gray mb-3">
          VISIBLE TO ROLES
        </p>

        <div className="w-full border-t border-gray-xx">
          {roles.map((role) => {
            const isChecked = selectedRoleIds.includes(role.id);
            return (
              <label
                key={role.id}
                className="flex items-center justify-between w-full py-3 border-b border-gray-xx text-left cursor-pointer"
              >
                <span className="text-sm text-xxx-gray">
                  {capitalize(role.name)}
                </span>
                <input
                  type="checkbox"
                  className="cursor-pointer"
                  checked={isChecked}
                  onChange={() => toggleRole(role.id)}
                />
              </label>
            );
          })}
        </div>
      </div>

      <RedButton
        onClickEvent={saveVisibility}
        styles="w-44 px-3 py-3 mt-8 mb-2 text-xs self-center"
        title={isSaving ? "SAVING" : "SAVE"}
        disabled={isSaving || isLoading}
      />
    </div>
  );
};

export default DocumentVisibilityModal;
